import {
    Bundle,
    bundleExtraProducts,
    FurnitureItem,
    getOriginalBundlePrice,
    toCartLineItem,
} from '../../data/furniture';
import * as Pr from '../../data/product';
import BreakboxBundles from '../../hardcodedProductData/moda/breakboxMap.json';
import * as BreakboxBundling from '../../data/breakboxBundling';

/** State for accessing the basket view and basket prices */
export const getBasketData = (items: Array<FurnitureItem>, bundles: Array<Bundle>) => {
    const { singleItemsPrice, totalPrice, productIdList, originalTotal, cart } = calculateCartData(
        items,
        bundles,
    );

    return {
        singleItemsPrice,
        totalPrice,
        productIdList,
        originalTotal,
        cart,
    };
};

function calculateCartData(
    items: Array<FurnitureItem>,
    bundles: Array<Bundle>,
): {
    singleItemsPrice: number;
    totalPrice: number;
    productIdList: string[];
    originalTotal: number;
    cart: Array<{ productId: string; quantity: number }>;
} {
    const singleItemsPrice = items
        .flatMap(furnitureItem => furnitureItem.products)
        .reduce((total, product) => total + product.price, 0);
    const totalBundlesPrice = bundles.reduce((total, bundle) => total + bundle.price, 0);
    const totalPrice = singleItemsPrice + totalBundlesPrice;

    const originalTotal =
        bundles.reduce((total, bundle) => total + getOriginalBundlePrice(bundle), 0) +
        singleItemsPrice;

    // Breakboxes are SKUs which refer to individual product items inside boxes. We use them in
    // our app to ensure the user can deal with individual 'products', but Moda requests that we
    // combine them into their standard 'boxed' SKUs before we hand the basket data back over.
    // To ease work in their warehouse.
    //
    // This list of possible Breakbox Bundles is generated as part of our ingest process, and
    // can be updated using veritas' `tash-pls/bbox` script.
    const breakboxBundling = BreakboxBundling.calculateBundling(
        BreakboxBundles,
        // we don't need to worry about the items already in a set bundle as they are presented
        // in the cart as a single set SKU.
        items
            .flatMap(i => i.products.map(p => Pr.productId(p)))
            .concat(bundles.flatMap(bundleExtraProducts)),
    );

    const productIds = breakboxBundling.bundles
        .map(bboxBundle => bboxBundle.canonicalId)
        .concat(breakboxBundling.singleItems);

    const bundleIds = bundles.map(bundle => bundle.canonicalId);

    const cartLineItems = toCartLineItem(productIds.concat(bundleIds));

    const productIdList = productIds.concat(bundleIds);

    const cart = Object.entries(cartLineItems).map(([productId, quantity]) => ({
        productId,
        quantity,
    }));

    return {
        singleItemsPrice,
        totalPrice,
        productIdList,
        originalTotal,
        cart,
    };
}
